import { useState, useEffect } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { creditQueries } from "@/api/credits";

interface CheckoutFormProps {
  amount: number;
  personaId: string;
  onSuccess: () => void;
  onReady: () => void;
}

export function CheckoutForm({
  amount,
  personaId,
  onSuccess,
  onReady,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (stripe && elements) {
      onReady();
    }
  }, [stripe, elements, onReady]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      // Confirm the payment without hard-coding a return_url
      // Use "redirect: 'if_required'" so Stripe only redirects if 3D Secure is needed
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        // If you do want a redirect after successful 3D Secure, specify a valid route:
        // confirmParams: {
        //   return_url: `${window.location.origin}/account?tab=credits#credits`,
        // },
      });

      if (error) {
        toast({
          variant: "destructive",
          description: error.message || "Payment failed",
        });
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        // Payment is successfully confirmed
        toast({
          title: "Payment successful",
          description: "Credits have been added to your account",
        });
        // Invalidate the query so UI credit/balance is re-fetched
        queryClient.invalidateQueries(creditQueries.agentBalance(personaId));
        onSuccess();
      }
    } catch (err) {
      console.error("Payment confirmation error:", err);
      toast({
        variant: "destructive",
        description: "An unexpected error occurred",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          `Pay $${amount}`
        )}
      </Button>
    </form>
  );
}
